<template>
  <div class="language-switcher"
       ref="root">
    <button type="button"
            class="trigger"
            :aria-label="$t('common.language')"
            :title="currentLocaleLabel"
            :aria-expanded="open ? 'true' : 'false'"
            aria-haspopup="listbox"
            @click="toggleMenu"
            @keydown="onTriggerKeydown">
      <span v-if="flagStyle"
            class="flag flag--svg"
            :style="flagStyle"></span>
      <span v-else
            class="flag">{{ currentFlag }}</span>
      <span class="trigger-label">{{ currentLocaleLabel }}</span>
      <span class="trigger-caret">▾</span>
    </button>

    <transition name="fade">
      <ul v-show="open"
          ref="menu"
          class="menu"
          role="listbox"
          :aria-activedescendant="activeDescendantId"
          @keydown="onMenuKeydown">
        <li v-for="(l, index) in locales"
            :id="`locale-option-${l.code}`"
            :key="l.code"
            class="option"
            role="option"
            :aria-selected="l.code === locale ? 'true' : 'false'"
            :class="{ 'is-selected': l.code === locale, 'is-active': index === activeIndex }"
            @mousemove="activeIndex = index"
            @click="selectLocale(l.code)">
          <span class="flag flag--svg"
                :style="flagStyleForLocale(l.code)"></span>
          <span class="option-label">{{ l.label }}</span>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import { setLocale, supportedLocales } from '../i18n'

export default {
  name: 'LanguageSwitcher',
  data() {
    return {
      open: false,
      activeIndex: -1,
    }
  },
  computed: {
    flagSvgByLocale() {
      return {
        'zh-CN':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="60" height="40" fill="#DE2910"/><polygon points="12,6 14.2,12.8 21.4,12.8 15.6,17 17.8,23.8 12,19.6 6.2,23.8 8.4,17 2.6,12.8 9.8,12.8" fill="#FFDE00"/></svg>',
        'en-US':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="60" height="40" fill="#B22234"/><g fill="#FFFFFF"><rect y="3.08" width="60" height="3.08"/><rect y="9.23" width="60" height="3.08"/><rect y="15.38" width="60" height="3.08"/><rect y="21.54" width="60" height="3.08"/><rect y="27.69" width="60" height="3.08"/><rect y="33.85" width="60" height="3.08"/></g><rect width="26" height="21.54" fill="#3C3B6E"/></svg>',
        'fr-FR':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="20" height="40" x="0" fill="#0055A4"/><rect width="20" height="40" x="20" fill="#FFFFFF"/><rect width="20" height="40" x="40" fill="#EF4135"/></svg>',
        'de-DE':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="60" height="13.33" y="0" fill="#000000"/><rect width="60" height="13.33" y="13.33" fill="#DD0000"/><rect width="60" height="13.34" y="26.66" fill="#FFCE00"/></svg>',
        'ja-JP':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="60" height="40" fill="#FFFFFF"/><circle cx="30" cy="20" r="10" fill="#BC002D"/></svg>',
        'ko-KR':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="60" height="40" fill="#FFFFFF"/><path d="M30 10a10 10 0 1 0 0 20a10 10 0 1 1 0-20z" fill="#CD2E3A"/><path d="M30 10a10 10 0 1 1 0 20a10 10 0 1 0 0-20z" fill="#0047A0"/></svg>',
        'es-ES':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="60" height="40" fill="#AA151B"/><rect width="60" height="20" y="10" fill="#F1BF00"/></svg>',
        'pt-PT':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="24" height="40" fill="#006600"/><rect x="24" width="36" height="40" fill="#FF0000"/></svg>',
        'ar-SA':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="60" height="40" fill="#006C35"/></svg>',
        'ms-MY':
          '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40"><rect width="60" height="40" fill="#CC0001"/><g fill="#FFFFFF"><rect y="3.08" width="60" height="3.08"/><rect y="9.23" width="60" height="3.08"/><rect y="15.38" width="60" height="3.08"/><rect y="21.54" width="60" height="3.08"/><rect y="27.69" width="60" height="3.08"/><rect y="33.85" width="60" height="3.08"/></g><rect width="26" height="21.54" fill="#010066"/><circle cx="13" cy="10.77" r="6" fill="#FFCC00"/></svg>',
      }
    },
    currentFlagSvg() {
      const svg = this.flagSvgByLocale[this.locale]
      if (!svg) return ''
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
    },
    flagStyle() {
      if (!this.currentFlagSvg) return null
      return { backgroundImage: `url("${this.currentFlagSvg}")` }
    },
    flagByLocale() {
      return {
        'zh-CN': '🇨🇳',
        'en-US': '🇺🇸',
        'fr-FR': '🇫🇷',
        'de-DE': '🇩🇪',
        'ja-JP': '🇯🇵',
        'ko-KR': '🇰🇷',
        'es-ES': '🇪🇸',
        'pt-PT': '🇵🇹',
        'ar-SA': '🇸🇦',
        'ms-MY': '🇲🇾',
      }
    },
    locale() {
      return this.$i18n.locale
    },
    locales() {
      return supportedLocales
    },
    currentLocale() {
      return this.locales.find((l) => l.code === this.locale) || null
    },
    currentFlag() {
      return this.flagByLocale[this.locale] || '🌐'
    },
    currentLocaleLabel() {
      return (this.currentLocale && this.currentLocale.label) || this.locale
    },
    activeDescendantId() {
      if (this.activeIndex < 0 || this.activeIndex >= this.locales.length) return ''
      const code = this.locales[this.activeIndex].code
      return `locale-option-${code}`
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick, true)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick, true)
  },
  methods: {
    flagStyleForLocale(code) {
      const svg = this.flagSvgByLocale[code]
      if (!svg) return null
      const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
      return { backgroundImage: `url("${dataUrl}")` }
    },
    onDocumentClick(e) {
      if (!this.open) return
      const root = this.$refs.root
      if (root && root.contains && root.contains(e.target)) return
      this.closeMenu()
    },
    openMenu() {
      this.open = true
      const currentIndex = this.locales.findIndex((l) => l.code === this.locale)
      this.activeIndex = currentIndex >= 0 ? currentIndex : 0
      this.$nextTick(() => {
        const menu = this.$refs.menu
        if (!menu) return
        const activeId = this.activeDescendantId
        if (!activeId) return
        const el = menu.querySelector(`#${CSS.escape(activeId)}`)
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    closeMenu() {
      this.open = false
    },
    toggleMenu() {
      if (this.open) this.closeMenu()
      else this.openMenu()
    },
    selectLocale(code) {
      setLocale(code)
      this.closeMenu()
    },
    moveActive(delta) {
      if (!this.locales.length) return
      const next = (this.activeIndex + delta + this.locales.length) % this.locales.length
      this.activeIndex = next
      this.$nextTick(() => {
        const menu = this.$refs.menu
        if (!menu) return
        const activeId = this.activeDescendantId
        if (!activeId) return
        const el = menu.querySelector(`#${CSS.escape(activeId)}`)
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    commitActive() {
      if (this.activeIndex < 0 || this.activeIndex >= this.locales.length) return
      this.selectLocale(this.locales[this.activeIndex].code)
    },
    onTriggerKeydown(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.toggleMenu()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!this.open) this.openMenu()
        else this.moveActive(1)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (!this.open) this.openMenu()
        else this.moveActive(-1)
        return
      }
      if (e.key === 'Escape') {
        if (!this.open) return
        e.preventDefault()
        this.closeMenu()
      }
    },
    onMenuKeydown(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.moveActive(1)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.moveActive(-1)
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        this.commitActive()
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        this.closeMenu()
      }
    },
  },
}
</script>

<style scoped>
.language-switcher {
  position: relative;
  display: inline-flex;
  background: transparent;
}

.flag {
  font-family: "Segoe UI Emoji", "Segoe UI Symbol", "Apple Color Emoji",
    "Noto Color Emoji", sans-serif;
  font-variant-emoji: emoji;
  font-size: 16px;
  line-height: 1;
}
.flag--svg {
  width: 20px;
  height: 14px;
  border-radius: 2px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: inline-block;
}

.trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: #333;
  padding: 2px 6px;
  cursor: pointer;
}

.trigger:focus {
  outline: none;
}

.trigger-label {
  font-size: 14px;
  white-space: nowrap;
}

.trigger-caret {
  font-size: 12px;
  line-height: 1;
}

.menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  max-height: 320px;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  margin: 0;
  padding: 6px;
  list-style: none;
  border-radius: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  z-index: 9999;
}

.menu::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
}

.option:hover,
.option.is-active {
  background: rgba(46, 162, 250, 0.12);
}

.option.is-selected {
  background: rgba(46, 162, 250, 0.18);
}

.option-label {
  font-size: 14px;
  color: #111;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
